import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }
#fadeToken = 0; #selectVariantMedia(mediaId) { const slideshow = this.slideshow; const slides = Array.from(this.querySelectorAll('slideshow-slide')); if (!slideshow || typeof slideshow.select !== 'function' || slides.length === 0) return false; let index = 0; if (mediaId != null) { const found = slides.findIndex((slide) => (slide.dataset.mediaId || slide.querySelector('[data-media-id]')?.dataset.mediaId) === String(mediaId)); if (found === -1) return false; index = found; } const token = ++this.#fadeToken; slideshow.style.transition = 'opacity 0.2s ease'; slideshow.style.opacity = '0'; setTimeout(() => { if (token !== this.#fadeToken) return; slideshow.select(index, undefined, { animate: false }); requestAnimationFrame(() => { slideshow.style.opacity = '1'; }); }, 200); return true; }
  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');
    if (newMediaGallery && this.dataset.variantMediaSelect === 'true' && newMediaGallery.dataset.variantMediaSelect === 'true' && !event.detail.data.newProduct && this.#selectVariantMedia(event.detail.resource?.featured_media?.id)) return;

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
